// Intake-logs portal — domain types.
//
// Operator-facing dashboard over the CloudWatch logs of the process-intake-form
// Lambdas. A scheduled Cloud Function (syncIntakeLogs) polls CloudWatch every
// 5 minutes, parses each Lambda invocation into a structured metadata row, and
// writes it to Firestore. The app reads those rows under the security rules;
// the RAW log lines (which contain form PHI) never land in Firestore — they
// are fetched on demand through the audited getIntakeLogEvents callable.
//
// Access model mirrors the recordings portal: a dedicated role axis at
// users/{uid}.restricted.logsRole, separate from the operator gate.

import { permLevel } from '../permissions/registry';

// ---------- Access role ----------
//
//   viewer → read the dashboard + raw drill-down (every raw view is audited)
//   admin  → viewer + manage roles, edit log-group config, read the audit log
export type LogsRole = 'viewer' | 'admin';

// Parsed outcome of one Lambda invocation. Derived from the lambdas' own
// "Success?: Success|Fail" metadata lines (their authoritative pass/fail
// signal), the "Skipping Processing" duplicate-form guard, and platform
// timeout lines. Rows with no definitive signal have NO status field —
// treat missing as 'unknown' in the UI.
export type InvocationStatus = 'success' | 'fail' | 'skipped';

// One parsed Lambda invocation. Doc id: `${lambda}_${request_id}` —
// deterministic, so the sync's overlapping re-reads upsert instead of
// duplicating. Written ONLY by the sync function (Admin SDK).
export interface IntakeLogInvocation {
  id: string;
  lambda: string; // log-group key from config: intake | intake_ai | ...
  log_group: string;
  log_stream: string;
  request_id: string;
  center: string | null; // client attribution — parsed "Current Center:" line
  session_id: string | null; // Zingtree session / Retell call id
  status: InvocationStatus | null; // null = no definitive signal parsed
  timed_out: boolean;
  error_summary: string | null; // parsed "Reporting issue:" line, truncated
  started_at: number; // epoch ms — first log event of the invocation
  ended_at: number; // epoch ms — last log event
  duration_ms: number | null; // from the platform REPORT line
  billed_ms: number | null;
  max_memory_mb: number | null;
  expires_at: number; // started_at + 14d; retention cleanup deletes past this
}

// One raw CloudWatch log line, returned by the getIntakeLogEvents callable.
export interface RawLogEvent {
  ts: number;
  message: string;
}

// A configured CloudWatch log group to sync. Editable by logs admins from the
// admin page (deployed lambda names can drift from the repo dir names).
export interface LogGroupConfig {
  key: string; // stable id, becomes IntakeLogInvocation.lambda
  label: string; // display name
  logGroup: string; // e.g. /aws/lambda/processIntakeForm
}

// Per-group sync checkpoint/status doc (intake_logs_sync/{key}).
export interface LogsSyncState {
  key: string; // doc id
  log_group: string;
  last_run_at: number;
  last_event_ts: number | null;
  last_scanned: number | null;
  last_invocations: number | null;
  truncated: boolean;
  last_error: string | null;
}

// Append-only audit row for raw-log access (raw lines contain PHI).
export interface LogsAccessAudit {
  id: string;
  user_id: string;
  email: string;
  action: 'view_raw';
  invocation_id: string;
  lambda: string | null;
  center: string | null;
  session_id: string | null;
  ip: string;
  user_agent: string;
  created_at: number;
}

// Filter inputs for the invocation list. All AND-combined; at most one
// categorical is pushed server-side (composite-index budget), the rest are
// applied client-side — same strategy as the recordings portal.
export interface LogFilters {
  center?: string; // exact match
  lambda?: string; // exact match on group key
  status?: InvocationStatus;
  search?: string; // client-side substring over session_id / center / error
  date_from?: number; // started_at >=
  date_to?: number; // started_at <=
}

// Resolve the caller's logs role from their stored restricted map. Returns
// null if the user has no logs access at all.
//
// Delegates to the unified permissions map first (restricted.permissions
// .intake_logs.level), falling back to the legacy restricted.logsRole field
// during the deprecation window.
export function resolveLogsRole(
  restricted:
    | { logsRole?: string; permissions?: Record<string, unknown> }
    | undefined
    | null,
): LogsRole | null {
  const v = permLevel(restricted, 'intake_logs') ?? restricted?.logsRole;
  switch (v) {
    case 'viewer':
    case 'admin':
      return v as LogsRole;
    default:
      return null;
  }
}
